/**
 * @fileoverview SubmitLinkRenderer component for the Storyteller web application.
 * Renders a clickable submit link with hover and active states.
 * Supports i18n translations and error handling.
 *
 * @example
 * // Basic usage
 * <SubmitLinkRenderer
 *   schema={{
 *     type: 'string',
 *     options: { text: 'Submit' }
 *   }}
 *   path="submit"
 *   handleChange={(path, value) => console.log(path, value)}
 * />
 */

import { LabelProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsLabelProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { MiddlewareContext } from '../../../../MiddlewareContext'
import css from './SubmitLinkRenderer.module.css'

/**
 * @function SubmitLinkRenderer
 * @description Component for rendering a clickable submit link with hover and active states.
 * @param {LabelProps & VanillaRendererProps} props - The props for the SubmitLinkRenderer component.
 * @returns {JSX.Element} The SubmitLinkRenderer component.
 */
function SubmitLinkRenderer(props: LabelProps & VanillaRendererProps) {
    const { submit, question } = useContext(MiddlewareContext)
    const { t } = useTranslation()
    const { text, uischema } = props
    const { answer } = uischema.options || {}

    async function onClick() {
        await submit(question.id, answer)
    }

    return (
        <button onClick={onClick} className={css.link}>
            {t(text)}
        </button>
    )
}

export const SubmitLinkTester = rankWith(3, uiTypeIs('SubmitLink'))

export default withVanillaControlProps(withJsonFormsLabelProps(SubmitLinkRenderer))
